import { createServer, type Server } from "node:http";
import { errorHtml, successHtml } from "./callback-html";

const CALLBACK_PORT = 13337;

// The redirect_uri uses the "localhost" hostname, which resolves to 127.0.0.1
// (IPv4) on some hosts and ::1 (IPv6) on others. Bind both loopback families so
// the browser's redirect connects regardless of how it resolves localhost.
const LOOPBACK_HOSTS = ["127.0.0.1", "::1"] as const;

export interface CallbackServerHandle {
	result: Promise<string>;
	close: () => void;
}

export interface CallbackServerOptions {
	port?: number | undefined;
	hosts?: readonly string[] | undefined;
	timeoutMs?: number | undefined;
}

/**
 * Binds a temporary HTTP server on the loopback callback port and resolves
 * once it is listening — so the caller can hand out the authorization URL only
 * when the redirect has somewhere to land. The returned `result` settles with
 * the authorization code from the `/callback` redirect carrying the expected
 * `state`, or rejects if the callback contains an error or the 5-minute
 * timeout elapses. Rejects up front if every loopback family fails to bind.
 */
export async function startCallbackServer(
	expectedState: string,
	options: CallbackServerOptions = {},
): Promise<CallbackServerHandle> {
	const servers: Server[] = [];
	let settled = false;
	const result = Promise.withResolvers<string>();
	const ready = Promise.withResolvers<void>();

	const closeServers = (): void => {
		for (const server of servers) {
			server.close();
			server.closeAllConnections();
		}
	};

	// Armed only after a listener is ready: this times out the wait for the
	// OAuth callback, not the bind, so it can never strand the startup await.
	let timeout: ReturnType<typeof setTimeout> | undefined;

	const settle = (run: () => void): void => {
		if (settled) return;
		settled = true;
		clearTimeout(timeout);
		closeServers();
		run();
	};

	let listening = 0;
	let bindFailures = 0;
	const hosts = options.hosts ?? LOOPBACK_HOSTS;
	for (const host of hosts) {
		const server = createServer((req, res) => {
			const url = new URL(req.url ?? "/", "http://localhost");

			// Ignore anything that isn't our callback (favicon, probes, prefetch)
			// so a stray request can't consume or abort the pending login.
			if (url.pathname !== "/callback") {
				res.writeHead(404, { "Content-Type": "text/plain; charset=utf-8" });
				res.end("Not found");
				return;
			}

			// Bind the response to this login: a missing/mismatched state is a
			// forged or cross-flow request — reject the request, not the login.
			if (url.searchParams.get("state") !== expectedState) {
				res.writeHead(400, { "Content-Type": "text/plain; charset=utf-8" });
				res.end("Invalid OAuth state");
				return;
			}

			const code = url.searchParams.get("code");
			const error = url.searchParams.get("error");

			res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
			// Settle only after the page has flushed — otherwise tearing the
			// socket down can reset the connection before the browser renders it.
			res.end(code ? successHtml : errorHtml(error), () => {
				settle(() => {
					if (code) result.resolve(code);
					else
						result.reject(
							new Error(`OAuth error: ${error ?? "no code returned"}`),
						);
				});
			});
		});

		server.on("listening", () => {
			listening += 1;
			if (listening === 1) ready.resolve();
		});

		// Tolerate a loopback family that isn't available (e.g. IPv6 disabled);
		// only fail if no family managed to bind.
		server.on("error", (error: NodeJS.ErrnoException) => {
			bindFailures += 1;
			if (bindFailures === hosts.length && listening === 0) {
				settle(() => ready.reject(error));
			}
		});

		server.listen(options.port ?? CALLBACK_PORT, host);
		servers.push(server);
	}

	await ready.promise;
	timeout = setTimeout(
		() =>
			settle(() =>
				result.reject(
					new Error("Timed out waiting for OAuth callback (5 min)."),
				),
			),
		options.timeoutMs ?? 5 * 60 * 1000,
	);
	return {
		result: result.promise,
		close: () =>
			settle(() => {
				// Swallow the rejection for callers that close without awaiting
				// result; anyone still awaiting it fails instead of hanging.
				result.promise.catch(() => {});
				result.reject(new Error("OAuth callback listener closed."));
			}),
	};
}
